/* eslint-disable react/no-unknown-property */
import { forbidExtraProps } from 'airbnb-prop-types';
import PropTypes from 'prop-types';
import React from 'react';

import { DayPickerNavigationPhrases } from '../defaultPhrases';
import getPhrasePropTypes from '../utils/getPhrasePropTypes';

import NavPositionShape from '../shapes/NavPositionShape';
import ScrollableOrientationShape from '../shapes/ScrollableOrientationShape';
import ChevronDown from './ChevronDown';
import ChevronUp from './ChevronUp';
import LeftArrow from './LeftArrow';
import RightArrow from './RightArrow';

import {
  HORIZONTAL_ORIENTATION,
  NAV_POSITION_BOTTOM,
  NAV_POSITION_TOP,
  VERTICAL_SCROLLABLE,
} from '../constants';
import mergeClassNames from '../utils/mergeClassNames';
import mergeStyles from '../utils/mergeStyles';

const propTypes = forbidExtraProps({
  disablePrev: PropTypes.bool,
  disableNext: PropTypes.bool,
  inlineStyles: PropTypes.object,
  isRTL: PropTypes.bool,
  navPosition: NavPositionShape,
  navPrev: PropTypes.node,
  navNext: PropTypes.node,
  orientation: ScrollableOrientationShape,

  onPrevMonthClick: PropTypes.func,
  onNextMonthClick: PropTypes.func,

  // internationalization
  phrases: PropTypes.shape(getPhrasePropTypes(DayPickerNavigationPhrases)),

  renderNavPrevButton: PropTypes.func,
  renderNavNextButton: PropTypes.func,
  showNavPrevButton: PropTypes.bool,
  showNavNextButton: PropTypes.bool,
});

const defaultProps = {
  disablePrev: false,
  disableNext: false,
  inlineStyles: null,
  isRTL: false,
  navPosition: NAV_POSITION_TOP,
  navPrev: null,
  navNext: null,
  orientation: HORIZONTAL_ORIENTATION,

  onPrevMonthClick() {},
  onNextMonthClick() {},

  // internationalization
  phrases: DayPickerNavigationPhrases,

  renderNavPrevButton: null,
  renderNavNextButton: null,
  showNavPrevButton: true,
  showNavNextButton: true,
};

class DayPickerNavigation extends React.PureComponent {
  render() {
    const {
      inlineStyles,
      isRTL,
      disablePrev,
      disableNext,
      navPosition,
      navPrev,
      navNext,
      onPrevMonthClick,
      onNextMonthClick,
      orientation,
      phrases,
      renderNavPrevButton,
      renderNavNextButton,
      showNavPrevButton,
      showNavNextButton,
    } = this.props;

    if (!showNavNextButton && !showNavPrevButton) {
      return null;
    }

    const isHorizontal = orientation === HORIZONTAL_ORIENTATION;
    const isVertical = orientation !== HORIZONTAL_ORIENTATION;
    const isVerticalScrollable = orientation === VERTICAL_SCROLLABLE;
    const isBottomNavPosition = navPosition === NAV_POSITION_BOTTOM;
    const hasInlineStyles = !!inlineStyles;

    let navPrevIcon = navPrev;
    let navNextIcon = navNext;
    let isDefaultNavPrev = false;
    let isDefaultNavNext = false;
    let navPrevTabIndex = {};
    let navNextTabIndex = {};

    if (!navPrevIcon && !renderNavPrevButton && showNavPrevButton) {
      navPrevTabIndex = { tabIndex: '0' };
      isDefaultNavPrev = true;
      let Icon = isVertical ? ChevronUp : LeftArrow;
      if (isRTL && !isVertical) {
        Icon = RightArrow;
      }
      navPrevIcon = (
        <Icon
          className={mergeClassNames(
            isHorizontal && 'DayPickerNavigation_svg__horizontal',
            isVertical && 'DayPickerNavigation_svg__vertical',
            disablePrev && 'DayPickerNavigation_svg__disabled',
          )}
        />
      );
    }

    if (!navNextIcon && !renderNavNextButton && showNavNextButton) {
      navNextTabIndex = { tabIndex: '0' };
      isDefaultNavNext = true;
      let Icon = isVertical ? ChevronDown : RightArrow;
      if (isRTL && !isVertical) {
        Icon = LeftArrow;
      }
      navNextIcon = (
        <Icon
          className={mergeClassNames(
            isHorizontal && 'DayPickerNavigation_svg__horizontal',
            isVertical && 'DayPickerNavigation_svg__vertical',
            disableNext && 'DayPickerNavigation_svg__disabled',
          )}
        />
      );
    }

    const isDefaultNav = isDefaultNavNext || isDefaultNavPrev;

    return (
      <div
        style={mergeStyles(
          hasInlineStyles && inlineStyles,
        )}
        className={mergeClassNames(
          'DayPickerNavigation',
          isHorizontal && 'DayPickerNavigation__horizontal',
          ...(isVertical ? [
            'DayPickerNavigation__vertical',
            isDefaultNav && 'DayPickerNavigation__verticalDefault',
          ] : []),
          ...(isVerticalScrollable ? [
            'DayPickerNavigation__verticalScrollable',
            isDefaultNav && 'DayPickerNavigation__verticalScrollableDefault',
            showNavPrevButton && 'DayPickerNavigation__verticalScrollable_prevNav',
          ] : []),
          ...(isBottomNavPosition ? [
            'DayPickerNavigation__bottom',
            isDefaultNav && 'DayPickerNavigation__bottomDefault',
          ] : []),
        )}
      >
        {showNavPrevButton
          && (renderNavPrevButton ? (
            renderNavPrevButton({
              ariaLabel: phrases.jumpToPrevMonth,
              disabled: disablePrev,
              onClick: disablePrev ? undefined : onPrevMonthClick,
              onKeyUp: disablePrev ? undefined : (e) => {
                const { key } = e;
                if (key === 'Enter' || key === ' ') {
                  onPrevMonthClick(e);
                }
              },
              onMouseUp: disablePrev ? undefined : (e) => {
                e.currentTarget.blur();
              },
            })
          ) : (
            <div // eslint-disable-line jsx-a11y/interactive-supports-focus
              role="button"
              {...navPrevTabIndex}
              className={mergeClassNames(
                'DayPickerNavigation_button',
                isDefaultNavPrev && 'DayPickerNavigation_button__default',
                disablePrev && 'DayPickerNavigation_button__disabled',
                ...(isHorizontal ? [
                  'DayPickerNavigation_button__horizontal',
                  ...(isDefaultNavPrev ? [
                    'DayPickerNavigation_button__horizontalDefault',
                    isBottomNavPosition
                      && 'DayPickerNavigation_bottomButton__horizontalDefault',
                    !isRTL && 'DayPickerNavigation_leftButton__horizontalDefault',
                    isRTL && 'DayPickerNavigation_rightButton__horizontalDefault',
                  ] : []),
                ] : []),
                ...(isVertical ? [
                  'DayPickerNavigation_button__vertical',
                  ...(isDefaultNavPrev ? [
                    'DayPickerNavigation_button__verticalDefault',
                    'DayPickerNavigation_prevButton__verticalDefault',
                    isVerticalScrollable
                      && 'DayPickerNavigation_prevButton__verticalScrollableDefault',
                  ] : []),
                ] : []),
              )}
              aria-disabled={disablePrev ? true : undefined}
              aria-label={phrases.jumpToPrevMonth}
              onClick={disablePrev ? undefined : onPrevMonthClick}
              onKeyUp={disablePrev ? undefined : (e) => {
                const { key } = e;
                if (key === 'Enter' || key === ' ') {
                  onPrevMonthClick(e);
                }
              }}
              onMouseUp={disablePrev ? undefined : (e) => {
                e.currentTarget.blur();
              }}
            >
              {navPrevIcon}
            </div>
          ))}

        {showNavNextButton
          && (renderNavNextButton ? (
            renderNavNextButton({
              ariaLabel: phrases.jumpToNextMonth,
              disabled: disableNext,
              onClick: disableNext ? undefined : onNextMonthClick,
              onKeyUp: disableNext ? undefined : (e) => {
                const { key } = e;
                if (key === 'Enter' || key === ' ') {
                  onNextMonthClick(e);
                }
              },
              onMouseUp: disableNext ? undefined : (e) => {
                e.currentTarget.blur();
              },
            })
          ) : (
            <div // eslint-disable-line jsx-a11y/interactive-supports-focus
              role="button"
              {...navNextTabIndex}
              className={mergeClassNames(
                'DayPickerNavigation_button',
                isDefaultNavNext && 'DayPickerNavigation_button__default',
                disableNext && 'DayPickerNavigation_button__disabled',
                ...(isHorizontal ? [
                  'DayPickerNavigation_button__horizontal',
                  ...(isDefaultNavNext ? [
                    'DayPickerNavigation_button__horizontalDefault',
                    isBottomNavPosition
                      && 'DayPickerNavigation_bottomButton__horizontalDefault',
                    isRTL && 'DayPickerNavigation_leftButton__horizontalDefault',
                    !isRTL && 'DayPickerNavigation_rightButton__horizontalDefault',
                  ] : []),
                ] : []),
                ...(isVertical ? [
                  'DayPickerNavigation_button__vertical',
                  ...(isDefaultNavNext ? [
                    'DayPickerNavigation_button__verticalDefault',
                    'DayPickerNavigation_nextButton__verticalDefault',
                    isVerticalScrollable
                      && 'DayPickerNavigation_nextButton__verticalScrollableDefault',
                  ] : []),
                ] : []),
              )}
              aria-disabled={disableNext ? true : undefined}
              aria-label={phrases.jumpToNextMonth}
              onClick={disableNext ? undefined : onNextMonthClick}
              onKeyUp={disableNext ? undefined : (e) => {
                const { key } = e;
                if (key === 'Enter' || key === ' ') {
                  onNextMonthClick(e);
                }
              }}
              onMouseUp={disableNext ? undefined : (e) => {
                e.currentTarget.blur();
              }}
            >
              {navNextIcon}
            </div>
          ))}
      </div>
    );
  }
}

DayPickerNavigation.propTypes = propTypes;
DayPickerNavigation.defaultProps = defaultProps;

export { DayPickerNavigation as PureDayPickerNavigation };
export default DayPickerNavigation;
